import React, { useEffect, useState } from 'react';
import {
  Container, Box, Typography, Button, Paper, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Alert,
  Divider, Chip, Tabs, Tab, Card, CardContent, CardActions, Tooltip, FormControl, InputLabel, Select,
  Stack, Badge, Collapse, ListItemIcon
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Add, Delete, Edit, ContentCopy, FilterList, Sort, 
  Home, Apartment, Gavel, Description, Moving, CheckCircle,
  Campaign, Search, PersonSearch, Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon, Home as HomeIcon,
  Apartment as ApartmentIcon, Gavel as GavelIcon,
  Moving as MovingIcon, Campaign as CampaignIcon,
  Search as SearchIcon, PersonSearch as PersonSearchIcon,
  Business, Business as BusinessIcon,
  Assignment, Assignment as AssignmentIcon,
  Event, Event as EventIcon,
  Payment, Payment as PaymentIcon,
  Security, Security as SecurityIcon,
  Build, Build as BuildIcon,
  CleaningServices, CleaningServices as CleaningServicesIcon,
  LocalShipping, LocalShipping as LocalShippingIcon,
  Inventory, Inventory as InventoryIcon,
  Assessment, Assessment as AssessmentIcon,
  ExpandMore, ExpandLess, AccessTime,
  DragIndicator,
  Save as SaveIcon,
  Close as CancelIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface Task {
  name: string;
  description: string;
  estimatedDuration: number;
  taskType: string;
  contractorType?: string; // Optional: for contractor_job tasks
  requiredFields?: Array<{
    name: string;
    type: string;
    label: string;
    required: boolean;
  }>;
}

interface Stage {
  name: string;
  description: string;
  color: string;
  icon: string;
  order: number;
  defaultTasks: Task[];
  showTasks?: boolean;
}

const defaultTask: Task = {
  name: '',
  description: '',
  estimatedDuration: 1,
  taskType: 'general',
  contractorType: undefined
};



const defaultStep: Stage = { 
  name: '', 
  description: '', 
  color: '#4CAF50',
  icon: 'check_circle',
  order: 1,
  defaultTasks: []
};

const iconOptions = [
  { name: 'home', label: 'Home', icon: <HomeIcon /> },
  { name: 'apartment', label: 'Apartment', icon: <ApartmentIcon /> },
  { name: 'gavel', label: 'Legal', icon: <GavelIcon /> },
  { name: 'description', label: 'Document', icon: <DescriptionIcon /> },
  { name: 'check_circle', label: 'Complete', icon: <CheckCircleIcon /> },
  { name: 'campaign', label: 'Marketing', icon: <CampaignIcon /> },
  { name: 'search', label: 'Search', icon: <SearchIcon /> },
  { name: 'person_search', label: 'Person Search', icon: <PersonSearchIcon /> },
  { name: 'business', label: 'Business', icon: <BusinessIcon /> },
  { name: 'assignment', label: 'Assignment', icon: <AssignmentIcon /> },
  { name: 'event', label: 'Event', icon: <EventIcon /> },
  { name: 'payment', label: 'Payment', icon: <PaymentIcon /> },
  { name: 'security', label: 'Security', icon: <SecurityIcon /> },
  { name: 'build', label: 'Build', icon: <BuildIcon /> },
  { name: 'cleaning_services', label: 'Cleaning', icon: <CleaningServicesIcon /> },
  { name: 'local_shipping', label: 'Shipping', icon: <LocalShippingIcon /> },
  { name: 'inventory', label: 'Inventory', icon: <InventoryIcon /> },
  { name: 'assessment', label: 'Assessment', icon: <AssessmentIcon /> }
];

// Task type options matching backend enum
const taskTypeOptions = [
  { value: 'general', label: 'General Task' },
  { value: 'contractor_job', label: 'Contractor Job' },
  { value: 'contract_upload', label: 'Contract Upload' },
  { value: 'styling_arrangement', label: 'Styling Arrangement' },
  { value: 'property_fixup', label: 'Property Fixup' },
  { value: 'price_range_determination', label: 'Price Range Determination' },
  { value: 'rent_range_determination', label: 'Rent Range Determination' },
  { value: 'management_fees_determination', label: 'Management Fees (Rental)' },
  { value: 'consultation_report', label: 'Consultation Report' },
  { value: 'sale_type_determination', label: 'Sale Type Determination' },
  { value: 'next_steps_determination', label: 'Next Steps Determination' },
  { value: 'present_findings', label: 'Present Findings' },
  { value: 'commission_rate', label: 'Commission Rate' },
  { value: 'open_scheduling', label: 'Open Scheduling' },
  { value: 'auction_scheduling', label: 'Auction Scheduling' },
  { value: 'auction_results', label: 'Auction Results' },
  { value: 'post_auction_if_sold', label: 'Post Auction If Sold' },
  { value: 'post_auction_if_passed_in', label: 'Post Auction If Passed In' },
  { value: 'post_auction_listing_update', label: 'Post Auction Listing Update' },
  { value: 'property_photography', label: 'Property Photography' },
  { value: 'sign_board_order', label: 'Sign Board Order' },
  { value: 'creating_listing', label: 'Creating Listing' },
  { value: 'receive_offers', label: 'Receive Offers' },
  { value: 'information_sheet_preparation', label: 'Information Sheet Preparation' },
  { value: 'property_valuation', label: 'Property Valuation' },
  { value: 'auction_decision', label: 'Auction Decision' },
  { value: 'auction_booking', label: 'Auction Booking' },
  { value: 'marketing_fee', label: 'Marketing Fee' },
  { value: 'staging_fee', label: 'Staging Fee' },
  { value: 'set_reserve_price', label: 'Set Reserve Price' },
  { value: 'agent_agreement', label: 'Prepare Agency Agreement' }
];

const WorkflowTemplates = ({ agencySlug }: { agencySlug: string }) => {
  console.log('WorkflowTemplates component rendered with agencySlug:', agencySlug);
  const { agency } = useAuth();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<{
    name: string;
    description: string;
    type: string;
    stages: Stage[];
  }>({
    name: '',
    description: '',
    type: 'sale',
    stages: [{ ...defaultStep }],
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categoryTab, setCategoryTab] = useState<'sale' | 'rental'>('sale');
  const [activeTab, setActiveTab] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [dragState, setDragState] = useState<{ stageIdx: number; taskIdx: number } | null>(null);
  const [stageDragIndex, setStageDragIndex] = useState<number | null>(null);
  const [editingStageNameIndex, setEditingStageNameIndex] = useState<number | null>(null);
  const [editingStageNameValue, setEditingStageNameValue] = useState<string>('');
  const [contractorTypes, setContractorTypes] = useState<Array<{ _id: string; name: string; slug: string }>>([]);

  // Resolve plan name: subscription.planName, subscriptionPlan, or default Free. Must be explicit so Free never gets copy.
  const planRaw = (agency?.subscription as any)?.planName ?? agency?.subscriptionPlan ?? 'Free';
  const planNormalized = String(planRaw).trim().toLowerCase();
  // Plans that allow copying/customizing workflow templates: agency and individual plans
  const CUSTOM_WORKFLOW_PLANS = ['professional', 'agency premium', 'premium', 'enterprise', 'demo', 'elite individual'];
  const canCopyTemplate = CUSTOM_WORKFLOW_PLANS.includes(planNormalized);

  // Function to determine if a template is a copied standard template
  const isCopiedStandardTemplate = editId ? (() => {
    const template = templates.find(t => t._id === editId);
    if (!template) return false;
    
    // A template is a copied standard template if:
    // 1. It has an agencyId (it's agency-specific)
    // 2. isDefault is false (it's not a system default)
    // 3. The name contains "(Custom)" (it was copied)
    return template.agencyId && !template.isDefault && template.name.includes('(Custom)');
  })() : false;

  const fetchTemplates = async () => {
    console.log('Fetching templates for agency:', agencySlug);
    setLoading(true);
    try {
      const response = await api.get(`/agencies/${agencySlug}/workflow-templates`);
      console.log('Templates fetched successfully:', response.data);
      setTemplates(response.data);
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    console.log('useEffect triggered with agencySlug:', agencySlug);
    fetchTemplates();
    fetchContractorTypes();
  }, [agencySlug]);

  const fetchContractorTypes = async () => {
    if (!agencySlug) return;
    try {
      const response = await api.get(`/agencies/${agencySlug}/contractor-types`);
      setContractorTypes(response.data || []);
    } catch (err) {
      console.error('Error fetching contractor types:', err);
    }
  };

  const handleOpen = (template?: any) => {
    setError('');
    setSuccess('');
    if (template) {
      setEditId(template._id);
      setForm({
        name: template.name,
        description: template.description,
        type: template.type,
        stages: template.stages || [{ ...defaultStep }],
      });
    } else {
      setEditId(null);
      setForm({ 
        name: '', 
        description: '', 
        type: 'sale', 
        stages: [{ ...defaultStep }] 
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleStageChange = (idx: number, field: string, value: string) => {
    const stages = [...form.stages];
    stages[idx] = { ...stages[idx], [field]: value };
    setForm({ ...form, stages });
  };

  const getIconComponent = (iconName: string) => {
    const option = iconOptions.find(opt => opt.name === iconName);
    return option ? option.icon : <CheckCircleIcon />;
  };

  const addStage = (insertAfterIndex: number = -1) => {
    const newStages = [...form.stages];
    const newStage = { 
      ...defaultStep, 
      order: insertAfterIndex === -1 ? form.stages.length + 1 : insertAfterIndex + 1,
      color: `#${Math.floor(Math.random()*16777215).toString(16)}` // Random color
    };
    
    if (insertAfterIndex === -1) {
      newStages.push(newStage);
    } else {
      newStages.splice(insertAfterIndex + 1, 0, newStage);
    }
    
    // Update order for all stages
    newStages.forEach((stage, index) => {
      stage.order = index + 1;
    });
    
    setForm({ ...form, stages: newStages });
  };

  const removeStage = (idx: number) => {
    const stages = form.stages.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
    setForm({ ...form, stages });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editId) {
        await api.put(`/agencies/${agencySlug}/workflow-templates/${editId}`, form);
        setSuccess('Template updated!');
      } else {
        await api.post(`/agencies/${agencySlug}/workflow-templates`, form);
        setSuccess('Template created!');
      }
      setOpen(false);
      fetchTemplates();
    } catch (err: any) {
      setError('Failed to save template');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/agencies/${agencySlug}/workflow-templates/${id}`);
      fetchTemplates();
    } catch {
      setError('Failed to delete template');
    }
  };

  const handleCopyTemplate = async (template: any) => {
    if (!canCopyTemplate) {
      setError('This feature requires Agency Premium or Elite Individual plan. Please upgrade to customize workflows.');
      return;
    }
    try {
      await api.post(`/agencies/${agencySlug}/workflow-templates/copy/${template._id}`);
      setSuccess('Template copied successfully!');
      fetchTemplates();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to copy template';
      setError(errorMessage);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'sale': return <Home />;
      case 'rental': return <Apartment />;
      default: return <Description />;
    }
  };

  const isSaleTemplate = (t: any) => t.type !== 'rental';
  const isRentalTemplate = (t: any) => t.type === 'rental';

  const templatesByCategory = {
    sale: templates
      .filter(isSaleTemplate)
      .sort((a, b) => {
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        if (sortBy === 'type') return (a.type || '').localeCompare(b.type || '');
        return 0;
      }),
    rental: templates
      .filter(isRentalTemplate)
      .sort((a, b) => {
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        if (sortBy === 'type') return (a.type || '').localeCompare(b.type || '');
        return 0;
      }),
  };

  const currentTemplates = templatesByCategory[categoryTab];
  const systemTemplates = currentTemplates.filter((t: any) => t.isDefault);
  const agencyTemplates = currentTemplates.filter((t: any) => !t.isDefault);

  const onTaskDragStart = (stageIdx: number, taskIdx: number) => (e: React.DragEvent) => {
    setDragState({ stageIdx, taskIdx });
    e.dataTransfer.effectAllowed = 'move';
  };

  const onTaskDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onTaskDrop = (targetStageIdx: number, targetTaskIdx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragState) return;
    const { stageIdx, taskIdx } = dragState;

    // Only allow reordering within the same stage (per requirement)
    if (stageIdx !== targetStageIdx) return;

    const newStages = [...form.stages];
    const tasks = [...(newStages[stageIdx].defaultTasks || [])];

    const [moved] = tasks.splice(taskIdx, 1);
    const insertAt = targetTaskIdx > taskIdx ? targetTaskIdx - 1 : targetTaskIdx;
    tasks.splice(insertAt, 0, moved);

    newStages[stageIdx].defaultTasks = tasks;
    setForm({ ...form, stages: newStages });
    setDragState(null);
  };

  const addTaskAfter = (stageIdx: number, afterTaskIdx: number) => {
    const newStages = [...form.stages];
    if (!newStages[stageIdx].defaultTasks) newStages[stageIdx].defaultTasks = [];
    newStages[stageIdx].defaultTasks.splice(afterTaskIdx + 1, 0, { ...defaultTask });
    setForm({ ...form, stages: newStages });
  };

  const onStageDragStart = (idx: number) => (e: React.DragEvent) => {
    setStageDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onStageDrop = (targetIdx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (stageDragIndex === null || stageDragIndex === targetIdx) return;
    const newStages = [...form.stages];
    const [moved] = newStages.splice(stageDragIndex, 1);
    const insertAt = targetIdx > stageDragIndex ? targetIdx - 1 : targetIdx;
    newStages.splice(insertAt, 0, moved);
    newStages.forEach((s, i) => { s.order = i + 1; });
    setForm({ ...form, stages: newStages });
    setStageDragIndex(null);
  };

  const onStageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const startEditStageName = (idx: number, current: string) => {
    setEditingStageNameIndex(idx);
    setEditingStageNameValue(current);
  };

  const saveEditStageName = (idx: number) => {
    const newStages = [...form.stages];
    newStages[idx] = { ...newStages[idx], name: editingStageNameValue };
    setForm({ ...form, stages: newStages });
    setEditingStageNameIndex(null);
    setEditingStageNameValue('');
  };

  const cancelEditStageName = () => {
    setEditingStageNameIndex(null);
    setEditingStageNameValue('');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
           <Typography variant="h4">Workflow Templates</Typography>
           <Button 
             variant="contained" 
             startIcon={<Add />} 
             onClick={() => handleOpen()}
           >
             New Template
           </Button>
         </Stack>



        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper sx={{ mb: 3, p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
                size="small"
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="type">Type</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        <Tabs
          value={categoryTab}
          onChange={(_, newValue) => {
            setCategoryTab(newValue as 'sale' | 'rental');
            setActiveTab(0);
          }}
          sx={{ mb: 2 }}
        >
          <Tab value="sale" label={`Sale (${templatesByCategory.sale.length})`} />
          <Tab value="rental" label={`Rental (${templatesByCategory.rental.length})`} />
        </Tabs>

        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label={`System Templates (${systemTemplates.length})`} />
          <Tab label={`Agency Templates (${agencyTemplates.length})`} />
        </Tabs>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Grid container spacing={3}>
            {(activeTab === 0 ? systemTemplates : agencyTemplates).map((tpl) => (
              <Grid item xs={12} md={6} key={tpl._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {tpl.name}
                        </Typography>
                        <Stack direction="row" spacing={1} mb={1}>
                          <Chip 
                            icon={getTypeIcon(tpl.type)}
                            label={tpl.type.charAt(0).toUpperCase() + tpl.type.slice(1)}
                            size="small"
                          />
                          {typeof tpl.version === 'number' && (
                            <Chip 
                              label={`v${tpl.version}`} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                          {tpl.isDefault && (
                            <Chip 
                              label="System Template" 
                              color="primary" 
                              size="small"
                            />
                          )}
                        </Stack>
                      </Box>
                      <Box>
                        {tpl.isDefault ? (
                          <Tooltip 
                            title={
                              canCopyTemplate 
                                ? "Copy template to customize workflows" 
                                : "Copying workflows is available on the Agency Premium or Elite Individual plan. Upgrade to copy and customize templates."
                            }
                          >
                            <span>
                              <IconButton 
                                onClick={() => canCopyTemplate && handleCopyTemplate(tpl)}
                                disabled={!canCopyTemplate}
                                sx={!canCopyTemplate ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                              >
                                <ContentCopy />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="Edit template">
                              <IconButton onClick={() => handleOpen(tpl)}>
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete template">
                              <IconButton onClick={() => handleDelete(tpl._id)}>
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      {tpl.description}
                    </Typography>

                    <List dense>
                      {tpl.stages?.map((stage: any, idx: number) => (
                        <React.Fragment key={idx}>
                          <ListItem 
                            sx={{ 
                              borderLeft: `4px solid ${stage.color}`,
                              mb: 1,
                              bgcolor: 'background.default'
                            }}
                          >
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle2">{stage.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ({stage.defaultTasks?.length || 0} tasks)
                                  </Typography>
                                  {stage.skippable && (
                                    <Chip 
                                      label="Skippable" 
                                      size="small" 
                                      color="warning" 
                                      variant="outlined"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={stage.description}
                            />
                            <IconButton 
                              size="small"
                              onClick={() => {
                                const newStages = [...tpl.stages];
                                newStages[idx].showTasks = !newStages[idx].showTasks;
                                setTemplates(templates.map(t => 
                                  t._id === tpl._id ? { ...t, stages: newStages } : t
                                ));
                              }}
                            >
                              {stage.showTasks ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </ListItem>
                          <Collapse in={stage.showTasks} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding dense>
                              {stage.defaultTasks?.map((task: any, taskIdx: number) => (
                                <ListItem 
                                  key={taskIdx}
                                  sx={{ 
                                    pl: 4,
                                    py: 0.5,
                                    '&:hover': {
                                      bgcolor: 'action.hover'
                                    }
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <AccessTime fontSize="small" color="action" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography>{task.name}</Typography>
                                        {task.taskType && task.taskType !== 'general' && (
                                          <Chip 
                                            label="Special" 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {task.description}
                                        </Typography>
                                        <Chip 
                                          size="small" 
                                          label={`${task.estimatedDuration} day${task.estimatedDuration > 1 ? 's' : ''}`}
                                          sx={{ ml: 1 }}
                                        />
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? 'Edit Template' : 'New Template'}
          {editId && (() => {
            const tpl = templates.find(t => t._id === editId);
            return (tpl && typeof tpl.version === 'number') ? (
              <Chip label={`v${tpl.version}`} size="small" sx={{ ml: 1 }} />
            ) : null;
          })()}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={form.name}
              onChange={handleFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={form.description}
              onChange={handleFormChange}
              margin="normal"
              multiline
              rows={2}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select
                name="type"
                value={form.type}
                label="Type"
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                required
                disabled={isCopiedStandardTemplate}
              >
                <MenuItem value="sale">Sale</MenuItem>
                <MenuItem value="rental">Rental</MenuItem>
              </Select>
            </FormControl>
            {isCopiedStandardTemplate && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Template type cannot be changed for copied standard templates
              </Typography>
            )}

                         <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Stages</Typography>
            {form.stages.map((stage, idx) => (
              <React.Fragment key={idx}>
                <Paper 
                  sx={{ p: 2, mb: 2 }}
                  draggable
                  onDragStart={onStageDragStart(idx)}
                  onDragOver={onStageDragOver}
                  onDrop={onStageDrop(idx)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DragIndicator fontSize="small" color="action" />
                      {editingStageNameIndex === idx ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            size="small"
                            value={editingStageNameValue}
                            onChange={(e) => setEditingStageNameValue(e.target.value)}
                            placeholder={`Stage ${idx + 1}`}
                          />
                          <IconButton size="small" onClick={() => saveEditStageName(idx)}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditStageName}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="subtitle1">{stage.name || `Stage ${idx + 1}`}</Typography>
                      )}
                    </Box>
                    <Box>
                      {editingStageNameIndex !== idx && (
                        <IconButton size="small" onClick={() => startEditStageName(idx, stage.name)}>
                          <Edit />
                        </IconButton>
                      )}
                      <Tooltip title={
                        stage.name === 'Market Appraisal' 
                          ? 'Market Appraisal stage cannot be deleted' 
                          : form.stages.length === 1 
                            ? 'Cannot delete the only stage' 
                            : 'Delete stage'
                      }>
                        <span>
                          <IconButton 
                            onClick={() => {
                              if (form.stages.length === 1) return;
                              if (stage.name === 'Market Appraisal') {
                                window.alert('Market Appraisal stage cannot be deleted as it is a special stage required for the consultation workflow.');
                                return;
                              }
                              if (!window.confirm('Delete this stage? All tasks in the stage will be removed.')) return;
                              removeStage(idx);
                            }}
                            disabled={form.stages.length === 1 || stage.name === 'Market Appraisal'}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <TextField
                    fullWidth
                    label="Description"
                    value={stage.description}
                    onChange={(e) => handleStageChange(idx, 'description', e.target.value)}
                    margin="normal"
                    multiline
                    rows={2}
                  />
                  <TextField
                    fullWidth
                    label="Color"
                    value={stage.color}
                    onChange={(e) => handleStageChange(idx, 'color', e.target.value)}
                    margin="normal"
                    type="color"
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Icon</InputLabel>
                    <Select
                      value={stage.icon}
                      label="Icon"
                      onChange={(e: SelectChangeEvent) => handleStageChange(idx, 'icon', e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getIconComponent(selected)}
                          <Typography>{iconOptions.find(opt => opt.name === selected)?.label}</Typography>
                        </Box>
                      )}
                    >
                      {iconOptions.map((option) => (
                        <MenuItem key={option.name} value={option.name}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {option.icon}
                            <Typography>{option.label}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>Tasks</Typography>
                  {stage.defaultTasks?.map((task: any, taskIdx: number) => {
                    // Check if task is special (cannot be deleted)
                    const specialTaskTypes = [
                      'styling_arrangement',
                      'price_range_determination',
                      'consultation_report',
                      'sale_type_determination',
                      'next_steps_determination',
                      'agent_agreement',
                      'present_findings',
                      'open_scheduling',
                      'property_photography',
                      'auction_results',
                      'post_auction_if_sold',
                      'post_auction_if_passed_in',
                      'set_weekly_rent'
                    ];
                    const isSpecialTaskType = task.taskType && specialTaskTypes.includes(task.taskType);
                    const isSetWeeklyRentTask = task.taskType === 'set_weekly_rent' || task.name === 'Set Weekly Rent';
                    const isPostAuctionStage = stage.name === 'Post-Auction' || stage.name?.toLowerCase().includes('post-auction');
                    const isSpecial = isSpecialTaskType || isSetWeeklyRentTask || isPostAuctionStage;
                    
                    return (
                    <Paper key={taskIdx} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}
                      draggable
                      onDragStart={onTaskDragStart(idx, taskIdx)}
                      onDragOver={onTaskDragOver}
                      onDrop={onTaskDrop(idx, taskIdx)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DragIndicator fontSize="small" color="action" />
                          Task {taskIdx + 1}
                          {isSpecial && (
                            <Chip 
                              label="Special" 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Box>
                          <Tooltip title={isSpecial ? 'Special tasks cannot be deleted' : 'Delete task'}>
                            <span>
                              <IconButton 
                                size="small"
                                onClick={() => {
                                  const newStages = [...form.stages];
                                  newStages[idx].defaultTasks = newStages[idx].defaultTasks.filter((_, i) => i !== taskIdx);
                                  setForm({ ...form, stages: newStages });
                                }}
                                disabled={isSpecial}
                              >
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                      <TextField
                        fullWidth
                        label="Task Name"
                        value={task.name}
                        onChange={(e) => {
                          const newStages = [...form.stages];
                          newStages[idx].defaultTasks[taskIdx].name = e.target.value;
                          setForm({ ...form, stages: newStages });
                        }}
                        margin="normal"
                        required
                      />
                      <TextField
                        fullWidth
                        label="Description"
                        value={task.description}
                        onChange={(e) => {
                          const newStages = [...form.stages];
                          newStages[idx].defaultTasks[taskIdx].description = e.target.value;
                          setForm({ ...form, stages: newStages });
                        }}
                        margin="normal"
                        multiline
                        rows={2}
                      />
                      <TextField
                        fullWidth
                        label="Estimated Duration (days)"
                        type="number"
                        value={task.estimatedDuration}
                        onChange={(e) => {
                          const newStages = [...form.stages];
                          newStages[idx].defaultTasks[taskIdx].estimatedDuration = parseInt(e.target.value) || 1;
                          setForm({ ...form, stages: newStages });
                        }}
                        margin="normal"
                        required
                        inputProps={{ min: 1 }}
                      />
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Task Type</InputLabel>
                        <Select
                          value={task.taskType || 'general'}
                          label="Task Type"
                          onChange={(e: SelectChangeEvent) => {
                            const newStages = [...form.stages];
                            newStages[idx].defaultTasks[taskIdx].taskType = e.target.value;
                            // Clear contractorType if task type is not contractor_job
                            if (e.target.value !== 'contractor_job') {
                              delete newStages[idx].defaultTasks[taskIdx].contractorType;
                            }
                            setForm({ ...form, stages: newStages });
                          }}
                        >
                          {taskTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      {task.taskType === 'contractor_job' && (
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Contractor Type</InputLabel>
                          <Select
                            value={task.contractorType || ''}
                            label="Contractor Type"
                            onChange={(e: SelectChangeEvent) => {
                              const newStages = [...form.stages];
                              newStages[idx].defaultTasks[taskIdx].contractorType = e.target.value;
                              setForm({ ...form, stages: newStages });
                            }}
                          >
                            {contractorTypes.length === 0 ? (
                              <MenuItem value="" disabled>No contractor types available</MenuItem>
                            ) : (
                              contractorTypes.map((type) => (
                                <MenuItem key={type._id} value={type.slug}>
                                  {type.name}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      )}
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button size="small" variant="text" startIcon={<Add />} onClick={() => addTaskAfter(idx, taskIdx)}>
                          Add Task After
                        </Button>
                      </Box>
                    </Paper>
                    );
                  })}
                  <Button
                    startIcon={<Add />}
                    onClick={() => {
                      const newStages = [...form.stages];
                      if (!newStages[idx].defaultTasks) {
                        newStages[idx].defaultTasks = [];
                      }
                      newStages[idx].defaultTasks.push({ ...defaultTask });
                      setForm({ ...form, stages: newStages });
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                  >
                    Add Task
                  </Button>
                </Paper>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Button
                    startIcon={<Add />}
                    onClick={() => addStage(idx)}
                    variant="outlined"
                    size="small"
                  >
                    Add Stage After This
                  </Button>
                </Box>
              </React.Fragment>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Button
                startIcon={<Add />}
                onClick={() => addStage()}
                variant="contained"
              >
                Add Stage at End
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WorkflowTemplates; 